import { gql, useLazyQuery, useMutation, useSubscription } from "@apollo/client";
import { Card, Collapse, Fade, Grid, Icon, Modal, Slide, Tooltip, Rating } from "@mui/material";
import logo from "assets/images/comedor.png";

import SoftBadge from "components/SoftBadge";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import SoftTypography from "components/SoftTypography";
import dayjs from "dayjs";
import PropTypes from "prop-types";
import { useEffect, useState, useContext } from "react";
import { getUproColor } from "utils/colors";
import { formatMoneyPunto, makeNiceText } from "utils/formatters";
import Anonimo from "assets/images/icons/anonimo.png";
import { MessageManager } from "context";
const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  maxHeight: "90%",
  width: { xs: "90%", sm: "40%", xxl: "30%" },
  overflowY: "auto",
  p: 3,
};

export default function ModalValorarPedido({ open, handleClose, idPedido, token, refetch }) {
  const [valoracionSabor, setValoracionSabor] = useState(0);
  const [valoracionCantidad, setValoracionCantidad] = useState(0);
  const [comentario, setComentario] = useState("");
  const [showContent, setShowContent] = useState(false);
  const [pedido, setPedido] = useState(null);
  const { handleSnackbar } = useContext(MessageManager);
  const [getPedido, { loading }] = useLazyQuery(
    gql`
      query pedido($id: ID!, $token: String) {
        pedido(id: $id, token: $token) {
          detalles {
            cantidad
            precio
            producto {
              nombre
              id
              categoriaProducto {
                categoria
              }
            }
          }
        }
      }
    `,
    {
      cachePolicy: "no-cache",
      fetchPolicy: "no-cache",
    }
  );

  useEffect(() => {
    if (open && idPedido) {
      getPedido({
        variables: {
          id: idPedido,
          token: token,
        },
      }).then((res) => {
        setPedido(res?.data?.pedido);
      });
    }
  }, [open]);

  useEffect(() => {
    if (loading) {
      setShowContent(false);
    }
  }, [loading]);

  const [valoracionPedido, { data: valoracionData, loading: loadingValoracion }] = useMutation(
    gql`
      mutation saveValoracionPlato($input: ValoracionPlatoInput!,$token: String) {
        saveValoracionPlato(input: $input, token: $token) {
          id
        }
      }
    `,
    {
      onCompleted: () => {
        refetch();
        handleClose();
        handleSnackbar("Valoración enviada correctamente", "success");
      },
      onError: (error) => {
        console.error("Error al enviar la valoración:", error);
        handleSnackbar("Error al enviar la valoración", "error");
      },
    }
  );

  return (
    <Modal
      aria-labelledby="transition-modal-title"
      aria-describedby="transition-modal-description"
      open={open}
      onClose={() => {
        handleClose();
      }}
      closeAfterTransition
    >
      <Fade in={open}>
        <Card sx={style}>
          <Fade
            in={loading}
            unmountOnExit
            mountOnEnter
            timeout={{ enter: 600, exit: 400 }}
            style={{ transitionTimingFunction: "ease-in-out" }}
            onExited={() => setShowContent(true)}
          >
            <SoftBox
              display="flex"
              justifyContent="center"
              alignItems="center"
              height="100%"
              minHeight="50vh"
            >
              <SoftBox
                display="flex"
                justifyContent="center"
                alignItems="center"
                flexDirection="column"
              >
                <SoftBox
                  component="img"
                  src={logo}
                  alt="UPrO Logo"
                  width="10rem"
                  p={3}
                  sx={{
                    animation: "blinking 1.5s infinite",
                    "@keyframes blinking": {
                      "0%": {
                        opacity: 1,
                      },
                      "50%": {
                        opacity: 0.5,
                      },
                      "100%": {
                        opacity: 1,
                      },
                    },
                  }}
                />
              </SoftBox>
            </SoftBox>
          </Fade>
          {showContent && (
            <Fade
              in={pedido && !loading}
              unmountOnExit
              mountOnEnter
              timeout={{ enter: 600, exit: 400 }}
              style={{ transitionTimingFunction: "ease-in-out" }}
            >
              <SoftBox>
                <SoftBox display="flex" justifyContent="space-between">
                  <SoftBox>
                    <SoftTypography variant="h6">Valora tu pedido</SoftTypography>
                  </SoftBox>
                  <SoftBox display="flex" justifyContent="space-between">
                    <SoftBox>
                      <Tooltip title="Cerrar" placement="top">
                        <Icon fontSize="medium" onClick={handleClose} sx={{ cursor: "pointer" }}>
                          close
                        </Icon>
                      </Tooltip>
                    </SoftBox>
                  </SoftBox>
                </SoftBox>
                <SoftBox pt={3}>
                  <SoftBox
                    display="flex"
                    flexDirection="column"
                    justifyContent="space-between"
                    gap={2}
                  >
                    {/* INFORMAR QUE LA VALORACION ES ANONIMA */}
                    <Slide in={open} direction="up" mountOnEnter unmountOnExit>
                      <SoftBox>
                        <Card
                          sx={{
                            width: "100%",
                          }}
                        >
                          <SoftBox
                            p={2}
                            sx={{
                              backgroundColor: getUproColor("uproGreen"),
                            }}
                          >
                            <SoftBox display="flex" justifyContent="center" alignItems="center">
                              <SoftBox
                                component="img"
                                src={Anonimo}
                                alt="Valoración anónima"
                                width="2.5rem"
                                mr={1}
                                sx={{
                                  animation: "shakes 1s",
                                  "@keyframes shakes": {
                                    "0%": { transform: "rotate(0deg)" },
                                    "25%": { transform: "rotate(10deg)" },
                                    "50%": { transform: "rotate(-20deg)" },
                                    "75%": { transform: "rotate(15deg)" },
                                    "100%": { transform: "rotate(-5deg)" },
                                  },
                                }}
                              />
                              <SoftTypography
                                variant="h6"
                                fontWeight="bold"
                                textAlign="center"
                                color="white"
                              >
                                Tu valoración es anónima
                              </SoftTypography>
                            </SoftBox>
                          </SoftBox>
                        </Card>
                      </SoftBox>
                    </Slide>

                    <Slide in={open} direction="up" mountOnEnter unmountOnExit>
                      <SoftBox>
                        <Card
                          sx={{
                            width: "100%",
                          }}
                        >
                          <SoftBox p={2} display="flex" flexDirection="column" alignItems="center">
                            <SoftBox display="flex" alignItems="center">
                              <SoftTypography variant="h2" fontWeight="bold" color="primary">
                                {(valoracionSabor + valoracionCantidad) / 2}
                              </SoftTypography>
                              <Icon color="warning" fontSize="large">
                                hotel_class
                              </Icon>
                            </SoftBox>
                          </SoftBox>
                        </Card>
                      </SoftBox>
                    </Slide>

                    <Slide in={open} direction="up" mountOnEnter unmountOnExit>
                      <SoftBox>
                        <Card
                          sx={{
                            width: "100%",
                          }}
                        >
                          <SoftBox p={2} display="flex" flexDirection="column" alignItems="center">
                            <SoftBox display="flex" alignItems="center">
                              <SoftBox mr={1} display="flex" alignItems="center">
                                <Icon color="primary">ramen_dining</Icon>
                              </SoftBox>
                              <SoftTypography variant="h6">Valoración de sabor</SoftTypography>
                            </SoftBox>
                            <SoftBox mt={2}>
                              <Rating
                                name="valoracion-sabor"
                                value={valoracionSabor}
                                precision={0.5}
                                onChange={(_, newValue) => setValoracionSabor(newValue)}
                                icon={
                                  <Icon color="warning" fontSize="large">
                                    star
                                  </Icon>
                                }
                                emptyIcon={
                                  <Icon color="secondary" fontSize="large">
                                    star_border
                                  </Icon>
                                }
                                sx={{ width: "100%" }}
                              />
                            </SoftBox>
                          </SoftBox>
                        </Card>
                      </SoftBox>
                    </Slide>

                    <Slide in={open} direction="up" mountOnEnter unmountOnExit>
                      <SoftBox>
                        <Card
                          sx={{
                            width: "100%",
                          }}
                        >
                          <SoftBox p={2} display="flex" flexDirection="column" alignItems="center">
                            <SoftBox display="flex" alignItems="center">
                              <SoftBox mr={1} display="flex" alignItems="center">
                                <Icon color="primary">soup_kitchen</Icon>
                              </SoftBox>
                              <SoftTypography variant="h6">Valoración de cantidad</SoftTypography>
                            </SoftBox>
                            <SoftBox mt={2}>
                              <Rating
                                name="valoracion-cantidad"
                                value={valoracionCantidad}
                                precision={0.5}
                                onChange={(_, newValue) => setValoracionCantidad(newValue)}
                                icon={
                                  <Icon color="warning" fontSize="large">
                                    star
                                  </Icon>
                                }
                                emptyIcon={
                                  <Icon color="secondary" fontSize="large">
                                    star_border
                                  </Icon>
                                }
                              />
                            </SoftBox>
                          </SoftBox>
                        </Card>
                      </SoftBox>
                    </Slide>
                    {/* Comentarios */}
                    <Slide in={open} direction="up" mountOnEnter unmountOnExit>
                      <SoftBox>
                        <Card
                          sx={{
                            width: "100%",
                          }}
                        >
                          <SoftBox p={2} display="flex" flexDirection="column" alignItems="center">
                            <SoftBox display="flex" alignItems="center">
                              <SoftBox mr={1} display="flex" alignItems="center">
                                <Icon color="primary">comment</Icon>
                              </SoftBox>
                              <SoftTypography variant="h6">Comentarios</SoftTypography>
                            </SoftBox>
                            <SoftBox mt={2} width="100%">
                              <SoftInput
                                value={comentario}
                                onChange={(e) => setComentario(e.target.value)}
                                placeholder="Escribe un comentario sobre tu pedido"
                                multiline
                                rows={3}
                                variant="outlined"
                                fullWidth
                                inputProps={{ maxLength: 150 }}
                              />
                              <SoftTypography
                                variant="caption"
                                color={comentario.length >= 150 ? "error" : "text"}
                                display="block"
                                textAlign="right"
                                mt={0.5}
                              >
                                {comentario.length} / 150 caracteres
                              </SoftTypography>
                            </SoftBox>
                          </SoftBox>
                        </Card>
                      </SoftBox>
                    </Slide>
                  </SoftBox>
                </SoftBox>
                <SoftBox mt={2} display="flex" justifyContent="space-between">
                  <SoftButton
                    circular
                    fullWidth
                    color="uproGreen"
                    onClick={() => {
                      valoracionPedido({
                        variables: {
                          token: token,
                          input: {
                            idPedido: idPedido,
                            idProducto: pedido?.detalles[0]?.producto?.id,
                            sabor: valoracionSabor,
                            cantidad: valoracionCantidad,
                            comentario: comentario,
                          },
                        },
                      });
                    }}
                    disabled={
                      valoracionSabor === 0 || valoracionCantidad === 0 || loadingValoracion
                    }
                  >
                    {valoracionSabor === 0 || valoracionCantidad === 0
                      ? "Completa las valoraciones"
                      : loadingValoracion
                      ? "Enviando..."
                      : "Enviar valoración"}
                  </SoftButton>
                </SoftBox>
              </SoftBox>
            </Fade>
          )}
        </Card>
      </Fade>
    </Modal>
  );
}

ModalValorarPedido.propTypes = {
  open: PropTypes.bool,
  handleClose: PropTypes.func,
  idPedido: PropTypes.string,
  token: PropTypes.string,
  refetch: PropTypes.func,
};
